import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api.service';
import { useTenantStore } from '../stores/tenant.store';
import { PayrollBatchDetailPanel } from '../components/payroll/PayrollBatchDetailPanel';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function monthStartIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function ars(n?: number | null) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}

function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function installmentLabel(row: any) {
  const ids = Array.isArray(row.installmentIds) ? row.installmentIds : [];
  const count = row.installmentCount ?? ids.length ?? 1;
  if (count > 1 && row.installmentFrom != null) {
    return `${row.installmentFrom}–${row.installmentTo} (${count})`;
  }
  if (count > 1) return `${row.installmentNumber} (${count})`;
  return row.installmentNumber;
}

export function PayrollDiscounts() {
  const tenantBranchId = useTenantStore((s) => s.branchId);
  const [entities, setEntities] = useState<any[]>([]);
  const [batches, setBatches] = useState<any[]>([]);
  const [pending, setPending] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({
    publicEntityId: '',
    from: monthStartIso(),
    to: todayIso(),
    fileDate: todayIso(),
  });
  const [preview, setPreview] = useState<any | null>(null);
  const [selectedDebt, setSelectedDebt] = useState<Record<string, boolean>>({});
  const [selectedMovements, setSelectedMovements] = useState<Record<string, boolean>>({});
  const [importBatchId, setImportBatchId] = useState('');
  const [importResult, setImportResult] = useState<any | null>(null);
  const [viewBatchId, setViewBatchId] = useState<string | null>(null);

  const loadMeta = async (entityId = filters.publicEntityId) => {
    try {
      const [ents, bats, movs] = await Promise.all([
        apiService.listPayrollPublicEntities(tenantBranchId || undefined),
        apiService.listPayrollDiscountBatches(),
        entityId ? apiService.listPayrollPendingMovements(entityId) : Promise.resolve([]),
      ]);
      setEntities(ents);
      setBatches(bats);
      setPending(movs);
      setSelectedMovements((prev) => {
        const next: Record<string, boolean> = {};
        for (const row of movs) next[row.id] = prev[row.id] !== false;
        return next;
      });
      setFilters((f) => {
        if (f.publicEntityId && ents.some((e: any) => e.id === f.publicEntityId)) return f;
        return { ...f, publicEntityId: ents[0]?.id ?? '' };
      });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantBranchId]);

  const debtRows: any[] = preview?.rows ?? [];
  const selectedInstallmentIds = useMemo(
    () =>
      debtRows
        .filter((r) => selectedDebt[r.loanId])
        .flatMap((r) => (Array.isArray(r.installmentIds) && r.installmentIds.length ? r.installmentIds : [r.installmentId])),
    [debtRows, selectedDebt],
  );
  const selectedMovementIds = useMemo(
    () => pending.filter((r) => selectedMovements[r.id]).map((r) => r.id),
    [pending, selectedMovements],
  );
  const debtTotals = useMemo(() => {
    return debtRows
      .filter((r) => selectedDebt[r.loanId])
      .reduce(
        (acc, r) => {
          acc.count += 1;
          acc.amount += Number(r.amount) || 0;
          acc.penalty += Number(r.penaltyAmount) || 0;
          acc.total += Number(r.totalAmount) || 0;
          return acc;
        },
        { count: 0, amount: 0, penalty: 0, total: 0 },
      );
  }, [debtRows, selectedDebt]);
  const movementTotals = useMemo(() => {
    return pending
      .filter((r) => selectedMovements[r.id])
      .reduce(
        (acc, r) => {
          acc.count += 1;
          acc.total += Number(r.totalAmount) || 0;
          return acc;
        },
        { count: 0, total: 0 },
      );
  }, [pending, selectedMovements]);

  const consult = async () => {
    setBusy(true);
    setError(null);
    setImportResult(null);
    try {
      const data = await apiService.previewPayrollDiscounts({
        publicEntityId: filters.publicEntityId,
        from: filters.from,
        to: filters.to,
      });
      setPreview(data);
      const next: Record<string, boolean> = {};
      for (const row of data.rows ?? []) next[row.loanId] = true;
      setSelectedDebt(next);
      await loadMeta(filters.publicEntityId);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const createMovements = async () => {
    if (!selectedInstallmentIds.length) {
      setError('Seleccione al menos una deuda libre para generar el movimiento');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await apiService.createPayrollDiscountMovements({
        publicEntityId: filters.publicEntityId,
        from: filters.from,
        to: filters.to,
        installmentIds: selectedInstallmentIds,
      });
      setImportResult({ created: result.created });
      await consult();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const generateFile = async () => {
    if (!selectedMovementIds.length) {
      setError('Seleccione movimientos pendientes para armar el lote');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await apiService.generatePayrollDiscounts({
        publicEntityId: filters.publicEntityId,
        from: filters.from,
        to: filters.to,
        fileDate: filters.fileDate,
        movementIds: selectedMovementIds,
      });
      downloadText(result.fileName, result.fileContent);
      await loadMeta(filters.publicEntityId);
      await consult();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    if (!window.confirm('¿Liberar envíos SENT sin respuesta para esta entidad?')) return;
    setBusy(true);
    setError(null);
    try {
      const result = await apiService.releasePayrollDiscounts({
        publicEntityId: filters.publicEntityId || undefined,
      });
      setImportResult({ released: result.released });
      await consult();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const onImportFiles = async (fileList: FileList | null) => {
    if (!importBatchId) {
      setError('Seleccione el lote a conciliar');
      return;
    }
    const files = fileList ? Array.from(fileList) : [];
    if (!files.length) return;
    setBusy(true);
    setError(null);
    try {
      const contents = await Promise.all(files.map((file) => file.text()));
      const result = await apiService.importPayrollDiscountResponse(importBatchId, contents);
      setImportResult(result);
      setViewBatchId(importBatchId);
      await loadMeta(filters.publicEntityId);
      await consult();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Generación archivo descuentos</h1>
      <p className="text-gray-600 mb-6">
        Primero se generan los movimientos de debe (atrasos + punitorios + cuota actual). El TXT del lote se arma después
        con esos movimientos ya existentes, para enviar a la entidad pública.
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}
      {importResult && (
        <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-green-800 text-sm">
          {importResult.created != null && <>Movimientos generados: {importResult.created}. </>}
          {importResult.released != null && <>Liberados: {importResult.released}. </>}
          {importResult.paid != null && (
            <>Cobrados: {importResult.paid} · Rechazados: {importResult.rejected} · Sin match: {importResult.unmatched}</>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg border p-4 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3">
        <select
          value={filters.publicEntityId}
          onChange={(e) => setFilters({ ...filters, publicEntityId: e.target.value })}
          className="px-3 py-2 border rounded-md"
        >
          <option value="">Entidad pública</option>
          {entities.map((e) => (
            <option key={e.id} value={e.id}>{e.code} — {e.name}</option>
          ))}
        </select>
        <label className="flex flex-col gap-1 text-xs text-gray-500">
          Desde (período)
          <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className="px-3 py-2 border rounded-md text-sm text-gray-900" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-500">
          Hasta (día de pago)
          <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className="px-3 py-2 border rounded-md text-sm text-gray-900" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-500">
          Fecha archivo
          <input type="date" value={filters.fileDate} onChange={(e) => setFilters({ ...filters, fileDate: e.target.value })} className="px-3 py-2 border rounded-md text-sm text-gray-900" />
        </label>
        <button
          type="button"
          disabled={busy || !filters.publicEntityId}
          onClick={consult}
          className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
        >
          {busy ? 'Consultando…' : 'Consultar'}
        </button>
      </div>

      <section className="mb-8">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold">1. Deuda libre → generar movimientos</h2>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="px-3 py-2 border rounded-md text-sm" onClick={() => {
              const next: Record<string, boolean> = {};
              for (const row of debtRows) next[row.loanId] = true;
              setSelectedDebt(next);
            }}>Marcar todo</button>
            <button type="button" className="px-3 py-2 border rounded-md text-sm" onClick={() => setSelectedDebt({})}>Desmarcar</button>
            <button type="button" disabled={busy} onClick={createMovements} className="px-3 py-2 bg-indigo-600 text-white rounded-md text-sm disabled:opacity-50">
              Generar movimientos
            </button>
          </div>
        </div>
        <div className="bg-white rounded-lg border overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2" />
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Legajo</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">DNI</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cuotas</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Punitorios</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total debe</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {debtRows.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-500">Consulte para ver deudas públicas libres hasta el día de pago.</td></tr>
              ) : debtRows.map((row) => (
                <tr key={row.loanId} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={!!selectedDebt[row.loanId]}
                      onChange={(e) => setSelectedDebt({ ...selectedDebt, [row.loanId]: e.target.checked })}
                    />
                  </td>
                  <td className="px-3 py-2">{row.employeeFileNumber}</td>
                  <td className="px-3 py-2">{row.documentNumber}</td>
                  <td className="px-3 py-2">{row.customerName}</td>
                  <td className="px-3 py-2">{installmentLabel(row)}</td>
                  <td className="px-3 py-2 text-right">{ars(row.amount)}</td>
                  <td className="px-3 py-2 text-right">{ars(row.penaltyAmount)}</td>
                  <td className="px-3 py-2 text-right font-medium">{ars(row.totalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-6 text-sm mt-3">
          <span>Seleccionados: <b>{debtTotals.count}</b></span>
          <span>Cuota: <b>{ars(debtTotals.amount)}</b></span>
          <span>Punitorios: <b>{ars(debtTotals.penalty)}</b></span>
          <span>Total: <b>{ars(debtTotals.total)}</b></span>
        </div>
      </section>

      <section className="mb-8">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold">2. Movimientos pendientes → armar lote / TXT</h2>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="px-3 py-2 border rounded-md text-sm" onClick={() => {
              const next: Record<string, boolean> = {};
              for (const row of pending) next[row.id] = true;
              setSelectedMovements(next);
            }}>Marcar todo</button>
            <button type="button" className="px-3 py-2 border rounded-md text-sm" onClick={() => setSelectedMovements({})}>Desmarcar</button>
            <button type="button" disabled={busy} onClick={generateFile} className="px-3 py-2 bg-emerald-600 text-white rounded-md text-sm disabled:opacity-50">
              Generar archivo
            </button>
            <button type="button" disabled={busy} onClick={release} className="px-3 py-2 border border-amber-400 text-amber-800 rounded-md text-sm disabled:opacity-50">
              Liberar envíos sin respuesta
            </button>
          </div>
        </div>
        <div className="bg-white rounded-lg border overflow-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2" />
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Legajo</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">DNI</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cuota</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Punitorios</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {pending.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-500">No hay movimientos pendientes para armar el lote.</td></tr>
              ) : pending.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={!!selectedMovements[row.id]}
                      onChange={(e) => setSelectedMovements({ ...selectedMovements, [row.id]: e.target.checked })}
                    />
                  </td>
                  <td className="px-3 py-2">{row.employeeFileNumber}</td>
                  <td className="px-3 py-2">{row.documentNumber}</td>
                  <td className="px-3 py-2">{row.customerName}</td>
                  <td className="px-3 py-2">{installmentLabel(row)}</td>
                  <td className="px-3 py-2 text-right">{ars(row.amount)}</td>
                  <td className="px-3 py-2 text-right">{ars(row.penaltyAmount)}</td>
                  <td className="px-3 py-2 text-right font-medium">{ars(row.totalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-6 text-sm mt-3">
          <span>En lote: <b>{movementTotals.count}</b></span>
          <span>Total a enviar: <b>{ars(movementTotals.total)}</b></span>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-3">Lotes enviados / importados</h2>
        <p className="text-sm text-gray-600 mb-3">
          Abrí un lote para ver cada línea del archivo y descargar el TXT/Excel enviado o la respuesta de la entidad.
        </p>
        <div className="bg-white rounded-lg border overflow-auto mb-4">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Entidad</th>
                <th className="px-3 py-2">Archivo</th>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2 text-right">Líneas</th>
                <th className="px-3 py-2 text-right">Monto</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {batches.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    Todavía no hay lotes. Generá el archivo en el paso 2.
                  </td>
                </tr>
              ) : (
                batches.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      {b.publicEntity?.code} — {b.publicEntity?.name}
                    </td>
                    <td className="px-3 py-2">{b.fileName ?? b.id.slice(0, 8)}</td>
                    <td className="px-3 py-2">
                      {b.fileDate ? new Date(b.fileDate).toLocaleDateString('es-AR') : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">{b.lineCount ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{ars(b.selectedAmount)}</td>
                    <td className="px-3 py-2">{b.status}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="text-indigo-600 hover:underline"
                        onClick={() => {
                          setViewBatchId(b.id);
                          setImportBatchId(b.id);
                        }}
                      >
                        Ver detalle
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {viewBatchId && (
          <PayrollBatchDetailPanel batchId={viewBatchId} onClose={() => setViewBatchId(null)} />
        )}
      </section>

      <section className="bg-white rounded-lg border p-4">
        <h2 className="font-semibold mb-3">3. Importar respuesta de la entidad</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
          <select
            value={importBatchId}
            onChange={(e) => {
              const id = e.target.value;
              setImportBatchId(id);
              if (id) setViewBatchId(id);
            }}
            className="px-3 py-2 border rounded-md"
          >
            <option value="">Lote generado</option>
            {batches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.fileName ?? b.id.slice(0, 8)} · {b.status} · {b.publicEntity?.code}
              </option>
            ))}
          </select>
          <input
            type="file"
            accept=".txt,text/plain,*/*"
            multiple
            disabled={busy}
            onChange={(e) => {
              void onImportFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Podés seleccionar varios archivos a la vez (ej. 2 de RESTO o 3 de Educación). Se suman líneas del mismo legajo.
        </p>
        {importResult?.errors?.length > 0 && (
          <ul className="mt-3 text-xs text-amber-800 space-y-1">
            {importResult.errors.slice(0, 20).map((err: { line: number; reason: string }, i: number) => (
              <li key={i}>Línea {err.line}: {err.reason}</li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
